import React, { ReactNode } from 'react'
import { Box, Stack } from '@mui/material'

import { PrescriptionData } from '@/shared/types/yakurekiPrescription'
import { SectionHeading } from '@/components/atoms/SectionHeading'
import { BodyText } from '@/components/atoms/BodyText'
import { MetaText } from '@/components/atoms/MetaText'

type Props = {
  currentPrescription?: PrescriptionData
  previousPrescription?: PrescriptionData
}

// 主役 = 薬剤名 (BodyText 14/primary). 補助 = MetaLine / 量 / 用法 / comment (MetaText 12/secondary).
// section heading は SectionHeading (caption/600/secondary). atom 3 つで完結.
const Section = ({
  title,
  children,
}: {
  title: string
  children?: ReactNode
}) => (
  <Box>
    <SectionHeading sx={{ mb: 0.75 }}>{title}</SectionHeading>
    <Box>{children}</Box>
  </Box>
)

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return ''
  try {
    const d = new Date(dateString)
    return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`
  } catch {
    return ''
  }
}

const MetaLine = ({ rx }: { rx: PrescriptionData }) => {
  const date = formatDate(rx.prescription_date)
  const parts = [
    date,
    rx.institution_name,
    rx.department_name,
    rx.doctor_name,
  ].filter(Boolean)
  if (parts.length === 0) return null
  return <MetaText sx={{ mb: 1 }}>{parts.join(' · ')}</MetaText>
}

const RpGroup = ({
  group,
}: {
  group: PrescriptionData['prescription_groups'][number]
}) => {
  const drugs = group.drugs ?? []
  const usageLine = [
    group.usage?.name,
    group.days != null ? `${group.days}日分` : '',
  ]
    .filter(Boolean)
    .join('  ')
  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr',
          columnGap: 1.5,
          rowGap: 0.25,
          alignItems: 'baseline',
        }}
      >
        {drugs.map((drug, dIdx) => {
          const qty = drug.unit ? `${drug.quantity}${drug.unit}` : drug.quantity
          return (
            <React.Fragment key={dIdx}>
              <BodyText>{drug.drug_name}</BodyText>
              <MetaText>{qty}</MetaText>
              {drug.drug_comment && (
                <MetaText sx={{ gridColumn: '1 / -1', fontStyle: 'italic' }}>
                  ※ {drug.drug_comment}
                </MetaText>
              )}
            </React.Fragment>
          )
        })}
      </Box>
      {usageLine && <MetaText sx={{ mt: 0.25 }}>{usageLine}</MetaText>}
      {group.usage?.usage_comment && (
        <MetaText sx={{ mt: 0.25, fontStyle: 'italic' }}>
          ※ {group.usage.usage_comment}
        </MetaText>
      )}
    </Box>
  )
}

const PrescriptionRows = ({ rx }: { rx: PrescriptionData }) => {
  const groups = rx.prescription_groups ?? []
  if (groups.length === 0) {
    return <MetaText sx={{ py: 0.5 }}>処方なし</MetaText>
  }
  return (
    <Stack spacing={1.25}>
      {groups.map((group, gIdx) => (
        <RpGroup key={gIdx} group={group} />
      ))}
    </Stack>
  )
}

const PrescriptionColumn = ({
  rx,
  title,
}: {
  rx: PrescriptionData
  title: string
}) => (
  <Section title={title}>
    <MetaLine rx={rx} />
    <PrescriptionRows rx={rx} />
  </Section>
)

const PrescriptionInfoTab: React.FC<Props> = ({
  currentPrescription,
  previousPrescription,
}) => {
  if (!currentPrescription && !previousPrescription) {
    return (
      <MetaText sx={{ py: 2, textAlign: 'center' }}>
        処方情報がありません
      </MetaText>
    )
  }

  if (!currentPrescription || !previousPrescription) {
    const rx = (currentPrescription ?? previousPrescription)!
    return (
      <Stack spacing={2}>
        <PrescriptionColumn rx={rx} title="処方" />
      </Stack>
    )
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        columnGap: 3,
        rowGap: 2,
      }}
    >
      <PrescriptionColumn rx={currentPrescription} title="今回" />
      <PrescriptionColumn rx={previousPrescription} title="前回" />
    </Box>
  )
}

export default PrescriptionInfoTab
